/**
 * Connexion WebSocket robuste pour les flux temps réel (monitoring, agent status).
 *
 * Caractéristiques :
 * - Reconnexion avec backoff exponentiel (1s → 30s max) pour ne pas DDoS le serveur
 *   en cas de panne prolongée.
 * - Heartbeat ping toutes les 25s ; si le serveur ne répond pas dans les 10s,
 *   la connexion est considérée morte et on reconnecte. Détecte les coupures
 *   réseau silencieuses (NAT timeout, proxy mort, etc.).
 * - JWT injecté en query-param `?token=…` (les WebSocket browser n'autorisent
 *   pas les headers custom). Le middleware backend lit ce token.
 */
type Handler = (msg: any) => void

const HEARTBEAT_INTERVAL_MS = 25_000
const HEARTBEAT_TIMEOUT_MS = 10_000
const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

export interface UseWebSocketOptions {
  /** Si vrai, ajoute `?token=…` depuis localStorage. Défaut: true. */
  withAuth?: boolean
}

export function useWebSocket(path: string, options: UseWebSocketOptions = {}) {
  const { withAuth = true } = options
  let ws: WebSocket | null = null
  let manuallyClosed = false
  let reconnectAttempt = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let heartbeatDeadlineTimer: ReturnType<typeof setTimeout> | null = null
  const handlers = new Map<string, Handler[]>()
  const stateListeners = new Set<(s: 'open' | 'closed' | 'reconnecting') => void>()

  function emitState(s: 'open' | 'closed' | 'reconnecting') {
    stateListeners.forEach((fn) => fn(s))
  }

  function clearReconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function clearHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    if (heartbeatDeadlineTimer) {
      clearTimeout(heartbeatDeadlineTimer)
      heartbeatDeadlineTimer = null
    }
  }

  function scheduleHeartbeat() {
    clearHeartbeat()
    heartbeatTimer = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      try {
        ws.send(JSON.stringify({ type: 'ping', t: Date.now() }))
      } catch {
        // sera détecté par le timer deadline ci-dessous
      }
      heartbeatDeadlineTimer = setTimeout(() => {
        // Aucune pong reçue → considère la connexion morte et reconnecte
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.close(4000, 'heartbeat-timeout')
          } catch {
            /* ignore */
          }
        }
      }, HEARTBEAT_TIMEOUT_MS)
    }, HEARTBEAT_INTERVAL_MS)
  }

  function noteAlive() {
    if (heartbeatDeadlineTimer) {
      clearTimeout(heartbeatDeadlineTimer)
      heartbeatDeadlineTimer = null
    }
  }

  function buildUrl(): string {
    const configured = (window as any).pexitalk?.config?.WS_BASE || import.meta.env.VITE_WS_BASE
    // Sans base configurée (build web servi par Caddy, pas d'Electron), on dérive
    // du même origine que la page : wss:// en HTTPS, ws:// sinon. Sinon `base`
    // valait `undefined` → URL `wss://host/undefined/ws/...` qui ne se connectait
    // jamais (même classe de bug que VITE_API_BASE absent pour médias/refresh).
    const base = configured
      || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
    let url = `${base}${path}`
    if (withAuth) {
      const token = localStorage.getItem('pexitalk-jwt')
      if (token) {
        const sep = url.includes('?') ? '&' : '?'
        url += `${sep}token=${encodeURIComponent(token)}`
      }
    }
    return url
  }

  function connect() {
    manuallyClosed = false
    clearReconnect()
    try {
      ws = new WebSocket(buildUrl())
    } catch {
      scheduleReconnect()
      return
    }
    ws.onopen = () => {
      reconnectAttempt = 0
      emitState('open')
      scheduleHeartbeat()
    }
    ws.onmessage = (ev) => {
      noteAlive()
      try {
        const msg = JSON.parse(ev.data)
        if (msg.type === 'pong') return // ack heartbeat, rien à propager
        ;(handlers.get(msg.type) || []).forEach((h) => h(msg))
      } catch {
        /* ignore */
      }
    }
    ws.onerror = () => {
      // onclose suivra, on laisse la reconnexion s'en occuper
    }
    ws.onclose = () => {
      clearHeartbeat()
      ws = null
      if (manuallyClosed) {
        emitState('closed')
        return
      }
      scheduleReconnect()
    }
  }

  function scheduleReconnect() {
    emitState('reconnecting')
    const backoff = Math.min(
      MAX_BACKOFF_MS,
      INITIAL_BACKOFF_MS * 2 ** Math.min(reconnectAttempt, 5),
    )
    // ±20% de jitter pour ne pas synchroniser tous les clients après reboot
    const jitter = backoff * (0.8 + Math.random() * 0.4)
    reconnectAttempt += 1
    reconnectTimer = setTimeout(connect, jitter)
  }

  function disconnect() {
    manuallyClosed = true
    clearReconnect()
    clearHeartbeat()
    if (ws) {
      ws.onclose = null
      try {
        ws.close()
      } catch {
        /* ignore */
      }
      ws = null
    }
    emitState('closed')
  }

  function on(type: string, handler: Handler) {
    const arr = handlers.get(type) || []
    arr.push(handler)
    handlers.set(type, arr)
  }

  function onStateChange(cb: (s: 'open' | 'closed' | 'reconnecting') => void) {
    stateListeners.add(cb)
    return () => stateListeners.delete(cb)
  }

  function send(msg: object): boolean {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    try {
      ws.send(JSON.stringify(msg))
      return true
    } catch {
      return false
    }
  }

  return { connect, disconnect, on, send, onStateChange }
}
